import Vue from "vue";
import Vuex from "vuex";
import { ActionTree } from "vuex";
const toId = require("to-id");
import { nanoid } from "nanoid/non-secure";
const _ = require("lodash");
import { getCurrentPositionFactory } from "@/lib/geolocationAsync.ts";

import mutations from "./mutations";
import { ShoppingList } from "@/models/ShoppingList";
import {
  ShoppingListEntry,
  RecipeShoppingListEntry,
  ManualShoppingListEntry,
} from "@/models/ShoppingListEntry";
import { Recipe } from "@/models/Recipe";
import { Meal } from "@/models/Meal";
import { Shop } from "@/models/Shop";
import { Geolocatable } from "@/models/Geolocation";

Vue.use(Vuex);

/*
The local PouchDB is the source of truth of most data.
We write changes directly to the local DB.
The store's state is only a mirror of that data.
Thus, the only mutation is refreshing the state from the DB.

Writing to the DB is asychronous, thus happens in actions.

(State that is not relevant to be persisted to DB is exempt from
this pattern, thus it may be handled with the state as the source of truth.)
*/

const PREFERENCES_DEFAULTS = {
  saveShoppedItemGeolocation: false,
  sentryErrorTrackingOptIn: false,
};

const POSITION_OPTIONS = {
  maximumAge: 120 * 1000,
  timeout: 20 * 1000,
  enableHighAccuracy: true,
};

class State {
  isInitializedFromPersistance: boolean = false;
  initialReplicationIsInProgress: boolean = false;
  initialReplicationPendingMaxCount: number | null = null;
  initialReplicationPendingCount: number | null = null;
  recipes: Array<Recipe> = [];
  shoppingList: ShoppingList | null = null;
  meals: Array<Meal> = [];
  shops: Array<Shop> = [];
  dbUrl: string;
  preferences: any;
  latestRoute: string | null;
  dismissedTutorialBoxIds: Array<string>;
  performanceMeasurementsRunning: any = {};
  performanceMeasurementsFinished: Array<{
    name: string;
    duration: number;
  }> = [];

  constructor() {
    this.shoppingList = new ShoppingList();
    this.dbUrl = localStorage.getItem("dbUrl") || "";
    this.preferences = _.defaults(
      JSON.parse(localStorage.getItem("preferences")) || {},
      PREFERENCES_DEFAULTS
    );
    this.latestRoute = localStorage.getItem("latestRoute") || null;
    this.dismissedTutorialBoxIds =
      JSON.parse(localStorage.getItem("dismissedTutorialBoxIds")) || [];
  }
}

const storeFactory = (
  db: any,
  navigator?: any,
  location?: any,
  overpassApiService?: any
) => {
  const getCurrentPosition = getCurrentPositionFactory(navigator);

  const actions = <ActionTree<State, any>>{
    async refreshFromPersistance(context) {
      context.commit("performanceMeasurementStart", "refreshFromPersistance");
      await Promise.all([
        await context.dispatch("refreshModelFromPersistance", "Meal"),
        await context.dispatch("refreshModelFromPersistance", "Recipe"),
        await context.dispatch("refreshModelFromPersistance", "Shop"),
        await context.dispatch("refreshEntriesFromPersistance"),
        await context.dispatch("refreshModelFromPersistance", "Archived"),
      ]);
      context.commit("finishedInitializationFromPersistance");
      context.commit("performanceMeasurementFinish", "refreshFromPersistance");
    },
    async refreshModelFromPersistance(context, modelName) {
      context.commit(
        "performanceMeasurementStart",
        `refreshModelFromPersistance-${modelName}`
      );
      const modelNamePlural = modelName + "s";
      const result = await db.allDocs({
        include_docs: true,
        attachments: true,
        startkey: `${modelName}:`,
        endkey: `${modelName}:\ufff0`,
      });
      context.commit(`refresh${modelNamePlural}FromDbData`, result);
      context.commit(
        "performanceMeasurementFinish",
        `refreshModelFromPersistance-${modelName}`
      );
    },
    async refreshEntriesFromPersistance(context) {
      const result = await db.allDocs({
        include_docs: true,
        startkey: `ManualShoppingListEntry:`,
        endkey: `RecipeShoppingListEntry:\ufff0`,
      });
      context.commit(`refreshEntriesFromDbData`, result);
    },
    async addMeal(context, meal) {
      const neededEntries = RecipeShoppingListEntry.fromRecipe(meal.recipe);

      const historicEntries = context.state.shoppingList.historicEntries.filter(
        (entry: ShoppingListEntry) => {
          return entry.isShopped && entry.shoppedAtPosition;
        }
      );

      neededEntries.forEach((entry) => {
        entry.setCommonShoppingPositionFromHistory(historicEntries);
      });

      const entryData = neededEntries.map((entry) => entry.intoPureObject());

      await db.bulkDocs([meal, ...entryData]);

      await Promise.all([
        await context.dispatch("refreshModelFromPersistance", "Meal"),
        await context.dispatch("refreshEntriesFromPersistance"),
      ]);
    },
    async clearMeals(context) {
      const deletedMeals = context.state.meals.map((meal: any) => {
        meal._deleted = true;
        return meal;
      });

      await db.bulkDocs(deletedMeals);
      await context.dispatch("refreshModelFromPersistance", "Meal");
    },
    async addRecipe(context, recipe: Recipe) {
      await db.put(recipe);
      await context.dispatch("refreshModelFromPersistance", "Recipe");
    },
    async saveRecipe(context, recipe: Recipe) {
      context.commit("performanceMeasurementStart", "saveRecipe");
      await db.put(recipe);
      await context.dispatch("refreshModelFromPersistance", "Recipe");
      context.commit("performanceMeasurementFinish", "saveRecipe");
    },
    async addImageToRecipe(context, data) {
      let updatedRecipe = _.cloneDeep(data.recipe);
      updatedRecipe._attachments = updatedRecipe._attachments || {};
      updatedRecipe._attachments["image.jpg"] = {
        content_type: "image/jpeg",
        data: data.image,
      };
      await db.put(updatedRecipe);
      await context.dispatch("refreshModelFromPersistance", "Recipe");
    },
    async saveShop(context, shop: Shop) {
      await db.put(shop);
      await context.dispatch("refreshModelFromPersistance", "Shop");
    },
    async markShopPreferred(
      context,
      data: { prefer: Shop; unprefer: Array<Shop> | undefined }
    ) {
      let preferredShop = _.clone(data.prefer);
      preferredShop.isPreferred = true;

      let unpreferredShops = [];

      if (data.unprefer) {
        unpreferredShops = data.unprefer.map((shop) => {
          let modifiedShop = _.clone(shop);
          modifiedShop.isPreferred = false;
          return modifiedShop;
        });
      }

      await db.bulkDocs([preferredShop, ...unpreferredShops]);
      await context.dispatch("refreshModelFromPersistance", "Shop");
    },
    async addManualShoppingListEntry(context, entry: ManualShoppingListEntry) {
      context.commit(
        "performanceMeasurementStart",
        "addManualShoppingListEntry"
      );
      entry.setCommonShoppingPositionFromHistory(
        context.state.shoppingList.historicEntries.filter(
          (entry: ShoppingListEntry) => {
            return entry.isShopped && entry.shoppedAtPosition;
          }
        )
      );
      await db.put(entry);
      await context.dispatch("refreshEntriesFromPersistance");
      context.commit(
        "performanceMeasurementFinish",
        "addManualShoppingListEntry"
      );
    },
    async removeShoppingListEntry(context, entry) {
      await db.remove(entry);
      await context.dispatch("refreshEntriesFromPersistance");
    },
    async toggleShoppingListEntryIsShopped(context, entry: ShoppingListEntry) {
      context.commit(
        "performanceMeasurementStart",
        "toggleShoppingListEntryIsShopped"
      );
      let modifiedEntry = _.clone(entry);
      modifiedEntry.toggleIsShopped();
      modifiedEntry.shoppedAtPosition = null;
      modifiedEntry.shoppedAt = null;
      if (modifiedEntry.isShopped) {
        modifiedEntry.shoppedAt = new Date();
      }

      const saveResult = await db.put(modifiedEntry);
      await context.dispatch("refreshEntriesFromPersistance");

      if (
        modifiedEntry.isShopped &&
        context.state.preferences.saveShoppedItemGeolocation &&
        navigator &&
        "geolocation" in navigator
      ) {
        const currentPosition: any = await getCurrentPosition(POSITION_OPTIONS);
        modifiedEntry.shoppedAtPosition = {
          latitude: currentPosition.coords.latitude,
          longitude: currentPosition.coords.longitude,
        };
        modifiedEntry._rev = saveResult.rev;

        await db.put(modifiedEntry);
        await context.dispatch("refreshEntriesFromPersistance");
      }
      context.commit(
        "performanceMeasurementFinish",
        "toggleShoppingListEntryIsShopped"
      );
    },
    async moveShoppingListEntryToShop(context, data) {
      context.commit(
        "performanceMeasurementStart",
        "moveShoppingListEntryToShop"
      );
      let modifiedEntry = _.clone(data.entry);
      modifiedEntry.commonShoppingPosition = data.shop.position;
      await db.put(modifiedEntry);
      await context.dispatch("refreshEntriesFromPersistance");
      context.commit(
        "performanceMeasurementFinish",
        "moveShoppingListEntryToShop"
      );
    },
    async archiveOldEntries(context) {
      context.commit("performanceMeasurementStart", "archiveOldEntries");
      const ONE_HOUR = 60 * 60 * 1000;
      let oldEntries = _.cloneDeep(context.state.shoppingList.entries).filter(
        (entry: ShoppingListEntry) => {
          return (
            entry.isShopped &&
            entry.shoppedAt &&
            new Date().valueOf() - entry.shoppedAt.valueOf() > ONE_HOUR * 24
          );
        }
      );

      let archivedEntries = _.cloneDeep(oldEntries).map(
        (entry: ManualShoppingListEntry | RecipeShoppingListEntry) => {
          delete entry._rev;
          entry._id = `Archived:${entry._id}`;
          return entry;
        }
      );

      oldEntries.forEach(
        (entry: ManualShoppingListEntry | RecipeShoppingListEntry) => {
          entry._deleted = true;
        }
      );

      await db.bulkDocs([...oldEntries, ...archivedEntries]);
      db.compact();
      context.commit("performanceMeasurementFinish", "archiveOldEntries");
      return Promise.all([
        context.dispatch("refreshEntriesFromPersistance"),
        context.dispatch("refreshModelFromPersistance", "Archived"),
      ]);
    },
    async deleteObsoleteArchivedEntries(context) {
      const obsoleteEntries =
        context.state.shoppingList.obsoleteHistoricEntries();

      const obsoleteEntriesDeleted = _.cloneDeep(obsoleteEntries).map(
        (entry: any) => {
          entry._deleted = true;
          return entry;
        }
      );

      await db.bulkDocs(obsoleteEntriesDeleted);
      db.compact();
      return Promise.all([
        context.dispatch("refreshEntriesFromPersistance"),
        context.dispatch("refreshModelFromPersistance", "Archived"),
      ]);
    },
    async loadNearShops(context, position: Geolocatable) {
      const nearShops = await overpassApiService.getCloseShops(position);
      if (nearShops.length) {
        await db.bulkDocs(nearShops);
        await context.dispatch("refreshModelFromPersistance", "Shop");
      }
    },
    async setDbUrl(context, data) {
      context.commit("saveDbUrl", data.url);
      if (data.resetLocal) {
        await db.destroy();
      }
      location.reload();
    },
    async runDataMigrations(context) {
      // Delete ManualShoppingListEntries without ingredient
      const docs = (
        await db.allDocs({
          include_docs: true,
          startkey: `ManualShoppingListEntry:`,
          endkey: `ManualShoppingListEntry:\ufff0`,
        })
      ).rows.map((row: any) => row.doc);

      const incompleteManualShoppingListEntries = docs
        .filter((entry: any) => typeof entry.ingredient === "undefined")
        .map((entry: any) => {
          let modifiedEntry = _.clone(entry);
          modifiedEntry._deleted = true;
          return modifiedEntry;
        });
      await db.bulkDocs(incompleteManualShoppingListEntries);

      /**
       * https://gitlab.com/pixelistik/meal-planner/-/issues/107
       */
      const ensureShoppedAtDate = context.state.shoppingList.entries
        .filter(
          (entry: ShoppingListEntry) => entry.isShopped && !entry.shoppedAt
        )
        .map((entry: ShoppingListEntry) => {
          let modifiedEntry = _.clone(entry);
          modifiedEntry.shoppedAt = new Date("2020-01-14");
          return modifiedEntry;
        });
      await db.bulkDocs(ensureShoppedAtDate);

      await context.dispatch("refreshFromPersistance");
    },
    doInitialReplication(context) {
      context.commit("initialReplicationStarted");
      return new Promise((resolve, reject) => {
        db.replicate
          .from(context.state.dbUrl)
          .on("change", (info: any) => {
            context.commit("initialReplicationUpdatePending", info.pending);
          })
          .on("complete", () => {
            context.dispatch("refreshFromPersistance");
            context.commit("initialReplicationStopped");
            resolve();
          })
          .on("error", (err: any) => {
            context.commit("initialReplicationStopped");
            reject(err);
          });
      });
    },
    setUpLiveReplication(context) {
      var opts = { live: true, retry: true };
      db.replicate.to(store.state.dbUrl, opts);
      db.replicate.from(store.state.dbUrl, opts).on("change", () => {
        context.dispatch("refreshFromPersistance");
      });
    },
  };

  let store = new Vuex.Store({
    strict: process.env.NODE_ENV !== "production",
    state: new State(),
    mutations,
    actions,
  });

  if (store.state.dbUrl) {
    store
      .dispatch("doInitialReplication")
      .then(() => {
        store.dispatch("setUpLiveReplication");
      })
      .catch(() => {
        store.dispatch("setUpLiveReplication");
      });
  }

  return store;
};

export default storeFactory;
